import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import useFetchInviteStatus, { InvitationStatusEnum } from '@shared/hooks/useFetchInviteQrCode';
import { SminexEventIcon } from '@shared/assets/icons';
import { palette } from '@shared/tokens/palette.stylex';
import { InviteStatus } from '@widgets/invite-status';
import { ErrorInvite } from '@widgets/invite-status/ui/error-invite';
import { getRouteQr } from '@shared/constants/router';
import useRespondInvite from '@features/invite/api/useRespondInvite';
import { PreloaderCircular } from '@shared/ui/kit';

export const InvitePage: FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();

  const { data, fetching, error, refetch } = useFetchInviteStatus(hash ?? '');
  const { accept, decline } = useRespondInvite();

  const [viewStatus, setViewStatus] = useState<InvitationStatusEnum | null>(null);
  const [viewAcceptionInfo, setViewAcceptionInfo] = useState<string | null>(null);

  useEffect(() => {
    if (data?.getInviteStatus?.invitationStatus) {
      setViewStatus(data.getInviteStatus.invitationStatus);
    }
  }, [data]);

  const handleRespondInvite = async (respondFn: typeof accept) => {
    if (!hash) return false;

    const response = (await respondFn(hash)).data?.acceptInvite;

    if (!response?.invitationStatus) return false;

    // Статус применяем и когда бэкенд отказал: он присылает актуальное состояние
    // приглашения — истёкший срок или блокировку, — и страница показывает его экран.
    setViewStatus(response.invitationStatus);
    setViewAcceptionInfo(response.inviteAcceptionInfo ?? '');

    return true;
  };

  if (fetching)
    return (
      <div {...stylex.props(styles.loader)}>
        <PreloaderCircular size="large" />
      </div>
    );

  if (error || !data?.getInviteStatus) {
    return (
      <div translate="no" {...stylex.props(styles.container)}>
        <SminexEventIcon colorPrimary={palette['grey-950']} colorSecondary={palette['bronze-500']} />

        <div {...stylex.props(styles.content)}>
          <ErrorInvite onRetry={refetch} />
        </div>
      </div>
    );
  }

  const { eventName, dateStart, dateEnd, ticketSent, inviteAcceptionInfo } = data.getInviteStatus;

  return (
    <div translate="no" {...stylex.props(styles.container)}>
      <SminexEventIcon colorPrimary={palette['grey-950']} colorSecondary={palette['bronze-500']} />

      <InviteStatus
        status={viewStatus ?? InvitationStatusEnum.PENDING}
        eventName={eventName}
        dateStart={dateStart}
        dateEnd={dateEnd}
        error={!!error}
        onRetry={refetch}
        onOpenTicket={() => navigate(getRouteQr(data?.getInviteStatus?.ticketHash))}
        ticketSent={ticketSent}
        onAccept={() => handleRespondInvite(accept)}
        onDecline={() => handleRespondInvite(decline)}
        inviteAcceptionInfo={viewAcceptionInfo ?? inviteAcceptionInfo}
      />
    </div>
  );
};

export default InvitePage;
